import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Typography, FormControl, Select, MenuItem, Stack,
  InputLabel, Tabs, Tab, CircularProgress, Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as salesCostsApi from '../../lib/api/salesCosts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Colors = {
  Gray: { header: '#F2F2F2', body: '#F9F9F9' },
  Blue: { header: '#E1F0F8', body: '#F1F9FF' },
  Orange: { header: '#FDF0E3', body: '#FFFBF7' },
  Green: { header: '#E8F5E9', body: '#F4FAF4' },
};

const StyledTableCell = styled(TableCell)({
  border: '1px solid #ccc',
  padding: '6px 8px',
  fontSize: '0.9rem',
  textAlign: 'right',
  whiteSpace: 'nowrap',
});

const HeaderCell = styled(StyledTableCell)({
  fontWeight: 'bold',
  textAlign: 'center',
  color: '#333',
  cursor: 'pointer',
});

const SalesMngCostStatsContainerViewNew = () => {
  const [viewDetail, setViewDetail] = useState({ mode: null, value: null });
  const [selectedYear, setSelectedYear] = useState('2026');
  const [mockData, setMockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const userStr = localStorage.getItem('user');
  const userInfo = userStr ? JSON.parse(userStr) : {};

  const dynamicYearList = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 2026;
    const endYear = currentYear;
    const years = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push({ value: String(y), label: `${y}년` });
    }
    return years;
  }, []);
  const months = Array.from({ length: 12 }, (_, i) => `${i + 1}월`);
  const categories = ['간접비(원가)', '직접비(원가)', '직접비', '전체공통비', '전략사업본부(공통)'];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await salesCostsApi.getSalesCostsStaticTotalViewNew(selectedYear);
        const result = response.data.list; 
        const filteredResult = userInfo.adminRole !== 'Y' 
          ? result.filter(item => item.dptUuid === userInfo.dptUuid)
          : result;
        const transformed = filteredResult.reduce((acc, curr) => {
          const teamName = curr.dptNm || '미지정팀';
          
          if (!acc[teamName]) {
            acc[teamName] = {
              id: teamName,
              values: Array(12).fill(0),
              details: Array.from({ length: 12 }, () => [0, 0, 0, 0, 0])
            };
          }

          const monthIdx = curr.dtMonth - 1;
          
          if (monthIdx >= 0 && monthIdx < 12) {
            const detailRow = [
              Number(curr.payIdCost || 0),
              Number(curr.payDrCost || 0),
              Number(curr.payDrExp || 0),
              Number(curr.payCorpCommon || 0),
              Number(curr.paySbuCmn || 0)
            ];

            const rowTotal = detailRow.reduce((a, b) => a + b, 0);

            acc[teamName].details[monthIdx] = detailRow;
            acc[teamName].values[monthIdx] = rowTotal;
          }

          return acc;
        }, {});

        const finalData = Object.values(transformed);
        setMockData(finalData);

      } catch (error) {
        console.error("데이터 로드 실패:", error);
        setMockData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear]);


  const detailStats = useMemo(() => {
    if (!viewDetail.mode) return null;

    const catTotals = Array(5).fill(0);
    let grandTotal = 0;

    if (viewDetail.mode === 'month') {
      mockData.forEach(team => {
        team.details[viewDetail.value].forEach((val, i) => { catTotals[i] += val; });
        grandTotal += team.values[viewDetail.value];
      });
    } else {
      const team = mockData.find(t => t.id === viewDetail.value);
      team.details.forEach(mDetail => {
        mDetail.forEach((val, i) => { catTotals[i] += val; });
      });
      grandTotal = team.values.reduce((a, b) => a + b, 0);
    }
    return { catTotals, grandTotal };
  }, [viewDetail, mockData]);

  const handleCellClick = (mode, val) => setViewDetail({ mode, value: val });
  const renderValPct = (val, total) => {
    const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0.0";
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center', 
        gap: 1.5
      }}>
        <span style={{ fontSize: '0.85rem' }}>
          {val.toLocaleString()}
        </span>
        <span style={{ 
          fontSize: '0.75rem', 
          color: '#1976d2', 
          fontWeight: 'bold',
          minWidth: '48px',
          textAlign: 'right',
          backgroundColor: 'rgba(25, 118, 210, 0.05)',
          padding: '2px 4px',
          borderRadius: '2px'
        }}>
          {pct}%
        </span>
      </Box>
    );
  };

  const handleMainExcelDownload = () => {
    const teamPrefix = userInfo.adminRole === 'Y' ? '전팀' : userInfo.dptNm || '';
    const fileName = `판매관리비_${selectedYear}_${teamPrefix}_전체현황`;
    
    const excelData = mockData.map(row => ({
      '구분': row.id,
      ...months.reduce((acc, m, i) => ({ ...acc, [m]: row.values[i] }), {}),
      '누계': row.values.reduce((a, b) => a + b, 0)
    }));

    downloadExcel(excelData, fileName);
  };

  const handleDetailExcelDownload = () => {
    if (!viewDetail.mode) return;

    let excelData = [];
    let fileName = "";

    if (viewDetail.mode === 'month') {
      fileName = `${selectedYear}_${viewDetail.value + 1}월_항목별상세`;
      excelData = mockData.map(team => ({
        '팀명': team.id,
        ...categories.reduce((acc, cat, i) => ({ ...acc, [cat]: team.details[viewDetail.value][i] }), {}),
        '합계': team.values[viewDetail.value]
      }));
    } else {
      fileName = `${selectedYear}_${viewDetail.value}_연간_항목별상세`;
      const team = mockData.find(t => t.id === viewDetail.value);
      excelData = months.map((m, mIdx) => ({
        '월': m,
        ...categories.reduce((acc, cat, i) => ({ ...acc, [cat]: team.details[mIdx][i] }), {}),
        '합계': team.values[mIdx]
      }));
    }

    downloadExcel(excelData, fileName);
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "데이터");
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer]), `${name}.xlsx`);
  };


  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center" 
        sx={{ mt: 3, mb: -1 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
          {selectedYear}년 판관비사용실적
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="year-select-label">조회 연도</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label="조회 연도"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ bgcolor: '#fff' }}
            >
              {dynamicYearList.map((y) => (
                <MenuItem key={y.value} value={y.value}>
                  {y.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -3, gap: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium', alignSelf: 'center' }}>
          (단위: 원)
        </Typography>
        <Button variant="contained" color="success" onClick={handleMainExcelDownload} size="small">
          엑셀 다운로드
        </Button>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table id="stats-table" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 1250 }}>
          <TableHead>
            <TableRow sx={{ height: '28px' }}> {/* 높이 더 축소 */}
              <HeaderCell sx={{ bgcolor: Colors.Gray.header, fontSize: '1rem', width: '90px' }}>
                구분
              </HeaderCell>
              {months.map((m, i) => (
                <HeaderCell 
                  key={i} 
                  onClick={() => handleCellClick('month', i)} 
                  sx={{ 
                    bgcolor: i < 6 ? Colors.Blue.header : Colors.Orange.header, 
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    width: '80px',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    '&:hover': {
                      color: '#1976d2',
                      fontWeight: 'bold'
                    }
                  }}
                >
                  {m}
                </HeaderCell>
              ))}
              <HeaderCell 
                sx={{ 
                  bgcolor: Colors.Green.header, 
                  fontSize: '1rem', 
                  width: '100px'
                }}
              >
                판관비 누계
              </HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mockData.map(row => (
              <TableRow key={row.id} sx={{ height: '28px' }} hover>
                <HeaderCell 
                  onClick={() => handleCellClick('team', row.id)} 
                  sx={{ 
                    bgcolor: Colors.Gray.body, 
                    cursor: 'pointer', 
                    fontSize: '0.9rem',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                    textDecorationColor: '#999',
                    '&:hover': {
                      color: '#1976d2',
                      textDecorationColor: '#1976d2' 
                    }
                  }}
                >
                  {row.id}
                </HeaderCell>
                {row.values.map((v, i) => (
                  <StyledTableCell 
                    key={i} 
                    onClick={() => handleCellClick('month', i)} 
                    sx={{ 
                      bgcolor: i < 6 ? Colors.Blue.body : Colors.Orange.body, 
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      textAlign: 'right',
                      pr: 1
                    }}
                  >
                    {v.toLocaleString()}
                  </StyledTableCell>
                ))}
                <StyledTableCell 
                  sx={{ 
                    bgcolor: Colors.Green.body, 
                    fontWeight: 'bold', 
                    fontSize: '0.9rem', 
                    textAlign: 'right', 
                    pr: 1.2
                  }}
                >
                  {row.values.reduce((a, b) => a + b, 0).toLocaleString()}
                </StyledTableCell>
              </TableRow>
            ))}
            {userInfo.adminRole === 'Y' && (
              <TableRow sx={{ height: '32px', borderTop: '2.5px solid #bbb' }}>
                <HeaderCell sx={{ bgcolor: '#eee', fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'center' }}>
                  전 팀 합계
                </HeaderCell>
                {months.map((_, i) => (
                  <StyledTableCell 
                    key={i} 
                    sx={{ bgcolor: i < 6 ? '#D1E6F3' : '#F5E1CC', fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'right', pr: 1 }}
                  >
                    {mockData.reduce((acc, cur) => acc + cur.values[i], 0).toLocaleString()}
                  </StyledTableCell>
                ))}
                <StyledTableCell 
                  sx={{ 
                    bgcolor: '#C8E6C9', 
                    fontWeight: '900', 
                    fontSize: '0.9rem', 
                    textAlign: 'right', 
                    pr: 1.2 
                  }}
                >
                  {mockData.reduce((acc, cur) => acc + cur.values.reduce((a, b) => a + b, 0), 0).toLocaleString()}
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {viewDetail.mode && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -3, gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium', alignSelf: 'center' }}>
              (단위: 원)
            </Typography>
            <Button variant="contained" color="success" onClick={handleDetailExcelDownload} size="small">
              엑셀 다운로드
            </Button>
          </Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
             ▶ {viewDetail.mode === 'month' ? `${viewDetail.value + 1}월 전 팀 항목별 비중` : `${viewDetail.value} 연간 항목별 비중`}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table id='detail-table' sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow sx={{ height: '29px' }}>
                  <HeaderCell rowSpan={2} sx={{ bgcolor: Colors.Gray.header, width: '14.28%', py: 0.4 }}>
                    구분
                  </HeaderCell>
                  <HeaderCell colSpan={5} sx={{ bgcolor: '#fff', py: 0.4 }}>
                    항목별 상세 (금액 / 비중%)
                  </HeaderCell>
                  <HeaderCell rowSpan={2} sx={{ bgcolor: Colors.Green.header, width: '14.28%', py: 0.4 }}>
                    합계
                  </HeaderCell>
                </TableRow>
                <TableRow sx={{ height: '29px' }}>
                  {categories.map(cat => (
                    <HeaderCell key={cat} sx={{ width: '14.28%', py: 0.4 }}>{cat}</HeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {viewDetail.mode === 'month' ? (
                  <>
                    {mockData.map(team => (
                      /* 바디 행: 37px */
                      <TableRow key={team.id} hover sx={{ height: '37px' }}>
                        <HeaderCell sx={{ width: '14.28%', bgcolor: Colors.Gray.body, py: 0.4 }}>{team.id}</HeaderCell>
                        {team.details[viewDetail.value].map((v, i) => (
                          <StyledTableCell 
                            key={i} 
                            sx={{ width: '14.28%', bgcolor: viewDetail.value < 6 ? Colors.Blue.body : Colors.Orange.body, py: 0.4 }}
                          >
                            {renderValPct(v, detailStats.catTotals[i])}
                          </StyledTableCell>
                        ))}
                        <StyledTableCell sx={{ width: '14.28%', fontWeight: 'bold', bgcolor: Colors.Green.body, py: 0.4 }}>
                          {renderValPct(team.values[viewDetail.value], detailStats.grandTotal)}
                        </StyledTableCell>
                      </TableRow>
                    ))}
                  </>
                ) : (
                  <>
                    {months.map((m, mIdx) => {
                      const team = mockData.find(t => t.id === viewDetail.value);
                      const isBlue = mIdx < 6;
                      return (
                        <TableRow key={m} hover sx={{ height: '37px' }}>
                          <HeaderCell sx={{ width: '14.28%', bgcolor: isBlue ? Colors.Blue.header : Colors.Orange.header, py: 0.4 }}>
                            {m}
                          </HeaderCell>
                          {team.details[mIdx].map((v, i) => (
                            <StyledTableCell 
                              key={i} 
                              sx={{ width: '14.28%', bgcolor: isBlue ? Colors.Blue.body : Colors.Orange.body, py: 0.4 }}
                            >
                              {renderValPct(v, detailStats.catTotals[i])}
                            </StyledTableCell>
                          ))}
                          <StyledTableCell sx={{ width: '14.28%', fontWeight: 'bold', bgcolor: Colors.Green.body, py: 0.4 }}>
                            {renderValPct(team.values[mIdx], detailStats.grandTotal)}
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}
                {(userInfo.adminRole === 'Y' || viewDetail.mode === 'team')  && (
                  <TableRow 
                    sx={{ 
                      height: '45px', 
                      borderTop: '2px solid #999', 
                      '& td, & th': { fontWeight: 'bold', width: '14.28%', py: 0.6 } 
                    }}
                  >
                    <HeaderCell sx={{ bgcolor: '#E0E0E0', color: '#333' }}>
                      {viewDetail.mode === 'team' ? '연간 합계' : '전 팀 합계'}
                    </HeaderCell>
                    {detailStats.catTotals.map((total, i) => {
                      const isBlue = viewDetail.mode === 'month' ? viewDetail.value < 6 : i < 6;
                      return (
                        <StyledTableCell 
                          key={i} 
                          sx={{ bgcolor: isBlue ? '#D1E6F3' : '#F5E1CC' }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                            <span style={{ fontSize: '0.88rem', color: '#333' }}>{total.toLocaleString()}</span>
                            <span style={{ 
                              fontSize: '0.72rem', 
                              color: '#444', 
                              minWidth: '42px',
                              textAlign: 'right',
                              backgroundColor: isBlue ? '#B8D5F0' : '#EBCFB2', 
                              padding: '1px 4px',
                              borderRadius: '2px'
                            }}>
                              100.0%
                            </span>
                          </Box>
                        </StyledTableCell>
                      );
                    })}
                    <StyledTableCell sx={{ bgcolor: '#C8E6C9' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '0.88rem', color: '#333' }}>{detailStats.grandTotal.toLocaleString()}</span>
                        <span style={{ 
                          fontSize: '0.72rem', 
                          color: '#1B5E20',
                          minWidth: '42px',
                          textAlign: 'right',
                          backgroundColor: '#A5D6A7', 
                          padding: '1px 4px',
                          borderRadius: '2px'
                        }}>
                          100.0%
                        </span>
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default SalesMngCostStatsContainerViewNew;
